import { useQuery } from '@tanstack/react-query'
import { AxiosError } from 'axios'
import { assets } from '@opallabs/api/v2'
import { TextEditorAsset } from '../../types'
import { queryKeys } from '../queryKeys'

const parseAssetTypeFromMimeType = (mimeType: string) => {
  const type = mimeType.split('/')[0]
  switch (type) {
    case 'image':
    case 'video':
    case 'audio':
      return type
    default:
      return 'file'
  }
}

/**
 * A document can reference an asset the viewer cannot read, for example one
 * pasted from another Opal. The node view then renders nothing for it, so the
 * failed fetch must not crash the whole document.
 */
const isInaccessibleAsset = (error: unknown) =>
  error instanceof AxiosError &&
  (error.response?.status === 403 || error.response?.status === 404)

export function useAsset(assetId: string, enabled = false) {
  return useQuery({
    queryKey: queryKeys.asset(assetId),
    enabled,
    retry: (failureCount, error) =>
      !isInaccessibleAsset(error) && failureCount < 3,
    useErrorBoundary: (error) => !isInaccessibleAsset(error),
    queryFn: () =>
      assets.readSingle({
        path: {
          asset_id: assetId,
        },
      }),
    select({ data }) {
      const asset: TextEditorAsset = {
        assetUuid: data.id,
        assetType: parseAssetTypeFromMimeType(data.attributes.mime_type),
        fullUrl: data.attributes.url,
        width: data.attributes.width,
        height: data.attributes.height,
        format: data.attributes.file_extension,
        filename: data.attributes.file_name,
        filesize: data.attributes.bytes,
      }

      return asset
    },
  })
}
